import { compare, coerce, valid } from 'semver';

/**
 * Normalizes a version string by ensuring it has major.minor.patch format.
 * Missing minor or patch versions are set to 0.
 * Preserves pre-release and build metadata (e.g., '-beta.1', '+build.1').
 *
 * @param version - Version string to normalize
 * @returns Normalized version string (e.g., '1.1' -> '1.1.0', '1.1-beta.1' -> '1.1.0-beta.1')
 */
const normalizeVersion = (version: string): string => {
  // If version is already valid semver (including pre-release/build metadata), use it as-is
  const validVersion = valid(version);
  if (validVersion) {
    return validVersion;
  }

  // Extract pre-release and build metadata if present
  const preReleaseMatch = version.match(/^([^-+]+)(-[^+]+)?(\+.+)?$/);
  if (!preReleaseMatch) {
    // If we can't parse it, return original
    return version;
  }

  const baseVersion = preReleaseMatch[1]; // e.g., '1.1' or '1.2.3'
  const preRelease = preReleaseMatch[2] || ''; // e.g., '-beta.1' or ''
  const build = preReleaseMatch[3] || ''; // e.g., '+build.1' or ''

  // Normalize the base version (add missing minor/patch)
  const coerced = coerce(baseVersion);
  if (coerced) {
    // Reconstruct with preserved metadata
    return `${coerced.version}${preRelease}${build}`;
  }

  // If coercion fails, manually pad missing parts
  const parts = baseVersion.split('.');
  const major = parts[0] || '0';
  const minor = parts[1] ?? '0';
  const patch = parts[2] ?? '0';

  const normalized = `${major}.${minor}.${patch}${preRelease}${build}`;
  return valid(normalized) ? normalized : version;
};

/**
 * Custom comparison function that treats pre-release versions as newer than release versions.
 * This is the opposite of standard semver behavior, but matches the business logic requirement
 * where beta versions are considered the "next version".
 *
 * @param versionA - First version to compare
 * @param versionB - Second version to compare
 * @returns Comparison result: positive if A > B, negative if A < B, 0 if equal
 */
const customCompare = (versionA: string, versionB: string): number => {
  const validA = valid(versionA);
  const validB = valid(versionB);

  if (!validA || !validB) {
    // Fallback to string comparison if versions aren't valid semver
    return versionB.localeCompare(versionA, undefined, { numeric: true });
  }

  // Extract base version (without pre-release/build metadata) for comparison
  const baseA = validA.split('-')[0].split('+')[0];
  const baseB = validB.split('-')[0].split('+')[0];

  // First compare base versions
  const baseComparison = compare(baseB, baseA); // Reverse for descending order
  if (baseComparison !== 0) {
    return baseComparison;
  }

  // If base versions are equal, check for pre-release tags
  const hasPreReleaseA = validA.includes('-');
  const hasPreReleaseB = validB.includes('-');

  // Versions with pre-release tags are considered newer than release versions
  // For descending order: if A is newer, return negative (A comes first)
  if (hasPreReleaseA && !hasPreReleaseB) {
    return -1; // A (with pre-release) is newer, so A comes first
  }
  if (!hasPreReleaseA && hasPreReleaseB) {
    return 1; // B (with pre-release) is newer, so B comes first
  }

  // If both have pre-release or both don't, use standard semver comparison
  // (reversed for descending order)
  return compare(versionB, versionA);
};

/**
 * Sorts semantic versions in descending order (newest first).
 *
 * Uses custom comparison that treats pre-release versions (e.g., beta) as newer than release versions.
 * Automatically normalizes versions missing minor or patch versions
 * (e.g., '1.1' becomes '1.1.0', '1' becomes '1.0.0').
 *
 * @param versions - The list of versions (semver) to sort
 * @returns The sorted list of versions in descending order
 * @example
 * sortVersions(['1.0.0', '2.0.0', '1.1.0']); // ['2.0.0', '1.1.0', '1.0.0']
 * sortVersions(['1.0.0', '1.0.0-beta.1']); // ['1.0.0-beta.1', '1.0.0'] (beta is newer)
 */
export const sortVersions = (versions: string[]): string[] => {
  return [...versions]
    .map(version => ({
      original: version,
      // Use original if it's valid semver (preserves pre-release/build metadata)
      // Otherwise normalize it
      normalized: valid(version) ? version : normalizeVersion(version),
    }))
    .sort((a, b) => {
      const versionA = valid(a.normalized) ? a.normalized : a.original;
      const versionB = valid(b.normalized) ? b.normalized : b.original;
      return customCompare(versionA, versionB);
    })
    .map(item => item.original);
};

/**
 * Returns the next major version while preserving the input format.
 *
 * Increments the major version by 1 and sets minor/patch to 0.
 * The output format matches the input format (number of version parts).
 *
 * @param version - Version string to increment
 * @returns Next major version in the same format as input
 * @example
 * getNextMajorVersion('1.2'); // '2.0'
 * getNextMajorVersion('1.2.3'); // '2.0.0'
 * getNextMajorVersion('1'); // '2'
 */
export const getNextMajorVersion = (version: string | number): string => {
  // Parse the version to get the major version number
  const coerced = coerce(version);
  if (!coerced) {
    // If we can't parse it, try to extract major version manually
    const parts = String(version).split('.');
    const major = parseInt(parts[0] || '0', 10);
    const nextMajor = major + 1;

    // Preserve the format: if input has N parts, output has N parts
    if (parts.length === 1) {
      return String(nextMajor);
    } else if (parts.length === 2) {
      return `${nextMajor}.0`;
    } else {
      return `${nextMajor}.0.0`;
    }
  }

  const nextMajor = coerced.major + 1;

  // Determine the original format by counting parts in the input
  const inputParts = String(version).split('.');
  const partCount = inputParts.length;

  // Reconstruct in the same format as input
  if (partCount === 1) {
    return String(nextMajor);
  } else if (partCount === 2) {
    return `${nextMajor}.0`;
  } else {
    return `${nextMajor}.0.0`;
  }
};
